/**
 * esoTime.js — Client-side port of getESOTime() from src/ai/intent.js.
 *
 * Computes the current in-game clock, calendar date, and moon phase for
 * Elder Scrolls Online. ESO time runs at 20× real-world speed so a full
 * game day is ~72 real minutes.
 *
 * Usage:
 *   import { getESOTime, moonGlyph } from '../lib/esoTime.js';
 *   const t = getESOTime();          // uses Date.now()
 *   const t = getESOTime(tsSeconds); // from a specific Unix timestamp (seconds)
 */

const TIME = {
  lengthOfDay:   20955,       // game day in real-world seconds
  lengthOfHour:  873.125,     // game hour in real-world seconds
  startTime:     1398033648.5,// reference timestamp at in-game noon
};

const DATE = {
  startTime:   1394617983.724,
  startWeekDay: 2,
  startYear:   582,
  startEra:    2,
  yearLength:  365,
  leaps:       141,
  monthLength: [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
};

const MOON = {
  startTime:                   1436153095,
  phaseLengthInSeconds:         628650,
  singlePhaseLengthInSeconds:    78581.25,
  phasesPercentageBetweenPhases:  0.125,
  fullEpoch:                  1777788000,
  loreJodeCycleSeconds:          628650,
  loreJoneCycleSeconds:          314325,
  phases: [
    { name: 'new',             endPercentage: 0.060 },
    { name: 'waxingCrescent',  endPercentage: 0.185 },
    { name: 'firstQuarter',    endPercentage: 0.310 },
    { name: 'waxingGibbous',   endPercentage: 0.435 },
    { name: 'full',            endPercentage: 0.560 },
    { name: 'waningGibbous',   endPercentage: 0.685 },
    { name: 'thirdQuarter',    endPercentage: 0.810 },
    { name: 'waningCrescent',  endPercentage: 0.935 },
  ],
};

const MONTH_NAMES = [
  "Morning Star", "Sun's Dawn", "First Seed", "Rain's Hand",
  "Second Seed", "Midyear", "Sun's Height", "Last Seed",
  "Hearthfire", "Frostfall", "Sun's Dusk", "Evening Star",
];

const DAY_NAMES = ["Morndas", "Tirdas", "Middas", "Turdas", "Fredas", "Loredas", "Sundas"];

const MOON_GLYPHS = {
  new:            '🌑',
  waxingCrescent: '🌒',
  firstQuarter:   '🌓',
  waxingGibbous:  '🌔',
  full:           '🌕',
  waningGibbous:  '🌖',
  thirdQuarter:   '🌗',
  waningCrescent: '🌘',
};

const MOON_LABELS = {
  new:            'New Moon',
  waxingCrescent: 'Waxing Crescent',
  firstQuarter:   'First Quarter',
  waxingGibbous:  'Waxing Gibbous',
  full:           'Full Moon',
  waningGibbous:  'Waning Gibbous',
  thirdQuarter:   'Third Quarter',
  waningCrescent: 'Waning Crescent',
};

function isLeapYear(y) {
  return y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
}

function phaseNameFromPct(pct) {
  let phase = 'waningCrescent';
  for (const p of MOON.phases) {
    if (pct < p.endPercentage) { phase = p.name; break; }
  }
  return phase;
}

function illuminationPercent(pct) {
  return Math.round((1 - Math.abs(pct * 2 - 1)) * 100);
}

function lorePhasePct(ts, cycleSeconds) {
  const sinceFull = ts - MOON.fullEpoch;
  const frac = ((sinceFull / cycleSeconds) % 1 + 1) % 1;
  return (frac + 0.5) % 1;
}

/** @param {number} [timestamp] Unix timestamp in **seconds** (defaults to now) */
export function getESOTime(timestamp) {
  const ts = timestamp ?? Date.now() / 1000;

  // ── Clock ──────────────────────────────────────────────────────────────
  const timeSinceStart = ts - TIME.startTime;
  const secondsSinceMidnight = ((timeSinceStart % TIME.lengthOfDay) + TIME.lengthOfDay) % TIME.lengthOfDay;
  const tst = 24 * secondsSinceMidnight / TIME.lengthOfDay;

  const hour = Math.floor(tst);
  const minuteDecimal = (tst - hour) * 60;
  const minute = Math.floor(minuteDecimal);

  const hour12 = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
  const period = hour < 12 ? 'AM' : 'PM';
  const timeString = `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
  const timeString24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const isDay = hour >= 6 && hour < 20;

  let timeOfDay = 'night';
  if (hour >= 5  && hour < 8)  timeOfDay = 'dawn';
  else if (hour >= 8  && hour < 12) timeOfDay = 'morning';
  else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
  else if (hour >= 17 && hour < 20) timeOfDay = 'dusk';

  // ── Date ───────────────────────────────────────────────────────────────
  const dateTimeSinceStart = ts - DATE.startTime;
  let daysPast = Math.floor(dateTimeSinceStart / TIME.lengthOfDay);

  const weekDay = ((daysPast + DATE.startWeekDay) % 7 + 7) % 7;
  const yearsSinceStart = Math.floor(daysPast / DATE.yearLength);
  let year = yearsSinceStart + DATE.startYear;

  let ly = year - 1;
  const leapsSinceStart = Math.floor(ly / 4) - Math.floor(ly / 100) + Math.floor(ly / 400) - DATE.leaps;
  daysPast = daysPast - yearsSinceStart * DATE.yearLength - leapsSinceStart;
  while (daysPast < 0) { year -= 1; daysPast += DATE.yearLength; }

  let month = 1;
  while (daysPast >= DATE.monthLength[month - 1]) {
    daysPast -= DATE.monthLength[month - 1];
    if (month === 2 && isLeapYear(year)) daysPast -= 1;
    month += 1;
  }
  const day = daysPast + 1;
  const monthName = MONTH_NAMES[month - 1];
  const dayName   = DAY_NAMES[weekDay];
  const dateString = `${dayName}, ${day} ${monthName}, ${DATE.startEra}E ${year}`;

  // ── Moon ───────────────────────────────────────────────────────────────
  const moonTimeSinceStart = ts - MOON.startTime;
  const secondsSinceNewMoon = ((moonTimeSinceStart % MOON.phaseLengthInSeconds) + MOON.phaseLengthInSeconds) % MOON.phaseLengthInSeconds;
  const phasePercentage = secondsSinceNewMoon / MOON.phaseLengthInSeconds;

  const moonPhase = phaseNameFromPct(phasePercentage);

  const isWaxing = phasePercentage <= MOON.phases[3].endPercentage;
  let pctFull = phasePercentage < MOON.phases[3].endPercentage
    ? phasePercentage * 2
    : phasePercentage < MOON.phases[4].endPercentage
      ? 1
      : 1 - (phasePercentage - 0.5) * 2;
  pctFull = Math.min(1, pctFull + pctFull * MOON.phasesPercentageBetweenPhases);

  const percentageOfNextPhase = phasePercentage % MOON.phasesPercentageBetweenPhases;
  const hoursUntilNextPhase = percentageOfNextPhase * 3.75 * 24;

  let secondsOffset = -phasePercentage * MOON.phaseLengthInSeconds;
  if (phasePercentage > MOON.phases[4].endPercentage) secondsOffset += MOON.phaseLengthInSeconds;
  const hoursUntilFull = (MOON.phases[3].endPercentage * MOON.phaseLengthInSeconds + secondsOffset) / TIME.lengthOfDay * 24;

  // Issue #119 — Jone (Secunda, the smaller moon) lags Jode by ~1/8 of
  // the synodic cycle, so the two moons show different glyphs even
  // though they share an underlying clock. We use the lore-canon offset
  // of ~3.5 days (≈ 12.5% of the in-game cycle) so the pair visibly
  // diverges instead of mirroring the same shape.
  const jonePctRaw = (phasePercentage + 0.125) % 1;
  const jonePhase = phaseNameFromPct(jonePctRaw);

  const loreJodePct = lorePhasePct(ts, MOON.loreJodeCycleSeconds);
  const loreJonePct = lorePhasePct(ts, MOON.loreJoneCycleSeconds);
  const loreJodePhase = phaseNameFromPct(loreJodePct);
  const loreJonePhase = phaseNameFromPct(loreJonePct);

  // Issue #119 — equivalent real-world (Earth) lunar phase. ESO uses a
  // shorter accelerated cycle than our actual moon, so the in-game
  // phase rarely matches what's hanging over your real window. The
  // synodic reference is the well-known 2000-01-06 18:14 UTC new moon.
  const REAL_NEW_MOON_TS = 947182440;       // seconds
  const REAL_SYNODIC     = 29.530588 * 86400; // seconds
  const realPct = (((ts - REAL_NEW_MOON_TS) % REAL_SYNODIC) + REAL_SYNODIC) % REAL_SYNODIC / REAL_SYNODIC;
  let realMoonPhase = 'waningCrescent';
  for (const p of MOON.phases) {
    if (realPct < p.endPercentage) { realMoonPhase = p.name; break; }
  }

  return {
    hour, minute, timeString, timeString24, isDay, timeOfDay,
    era: DATE.startEra, year, month, monthName, day, weekDay, dayName, dateString,
    moonPhase, moonLabel: MOON_LABELS[moonPhase], moonGlyph: MOON_GLYPHS[moonPhase],
    // Per-moon glyphs (Jode = Masser, Jone = Secunda) — #119.
    jodeGlyph: MOON_GLYPHS[moonPhase],
    jodePhase: moonPhase,
    joneGlyph: MOON_GLYPHS[jonePhase],
    jonePhase,
    esoJodeIlluminationPercent: illuminationPercent(phasePercentage),
    esoJoneIlluminationPercent: illuminationPercent(phasePercentage),
    loreJodePhase,
    loreJonePhase,
    loreJodeGlyph: MOON_GLYPHS[loreJodePhase],
    loreJoneGlyph: MOON_GLYPHS[loreJonePhase],
    loreJodeIlluminationPercent: illuminationPercent(loreJodePct),
    loreJoneIlluminationPercent: illuminationPercent(loreJonePct),
    // Real-world equivalent — what your local sky is doing right now.
    realMoonPhase,
    realMoonGlyph: MOON_GLYPHS[realMoonPhase],
    realMoonLabel: MOON_LABELS[realMoonPhase],
    moonFullnessPercent: pctFull * 100,
    moonWaxing: isWaxing,
    hoursUntilNextPhase,
    hoursUntilFull,
  };
}

/** Return the Unicode moon glyph for a named phase. */
export function moonGlyph(phase) {
  return MOON_GLYPHS[phase] || '🌑';
}
